// src/components/HomePage/RecentTestedServers.jsx
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatTimeAgo } from '../../utils/timeUtils';

// Fallback presets matching page.html
const DEFAULT_PRESETS = [
  {
    ip: 'mc.hypixel.net:25565',
    name: 'Hypixel Network',
    players: '48,290',
    timeAgo: '5 минут назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
        <path d="M4 4h4v6h8V4h4v16h-4v-6H8v6H4z" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <path d="M20 20h20v25h20v-25h20v60h-20v-25h-20v25h-20z" />
        <polygon points="50,5 58,15 42,15" />
      </svg>
    )
  },
  {
    ip: 'play.funtime.su:25565',
    name: 'FunTime Anarchy',
    players: '7,840',
    timeAgo: '14 минут назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
        <polygon points="12,2 15,8 22,9 17,14 18,21 12,17 6,21 7,14 2,9 9,8" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <polygon points="50,10 62,38 92,40 68,60 76,90 50,72 24,90 32,60 8,40 38,38" />
      </svg>
    )
  },
  {
    ip: 'mc.reallyworld.ru:25565',
    name: 'ReallyWorld',
    players: '12,150',
    timeAgo: '28 минут назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
        <polygon points="12,2 22,8 12,14 2,8" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <polygon points="50,15 85,35 50,55 15,35" />
        <polygon points="15,40 50,60 50,90 15,70" />
        <polygon points="50,60 85,40 85,70 50,90" />
      </svg>
    )
  },
  {
    ip: 'anarchy.2b2t.org:25565',
    name: '2B2T Anarchy',
    players: '920',
    timeAgo: '1 час назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-none stroke-white" strokeWidth="2">
        <circle cx="12" cy="12" r="9" />
        <path d="M6 18L12 6l6 12M7 14h10" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#ffffff" strokeWidth="8" />
        <path d="M25 75L50 25l25 50M30 60h40" stroke="#ffffff" strokeWidth="8" strokeLinecap="round" />
      </svg>
    )
  },
  {
    ip: 'vanilla-craft.net:25565',
    name: 'Vanilla Craft',
    players: '140',
    timeAgo: '2 часа назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
        <rect x="4" y="4" width="16" height="16" rx="3" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <rect x="20" y="20" width="60" height="60" rx="8" fill="#ffffff" />
      </svg>
    )
  },
  {
    ip: 'mc.holyworld.ru:25565',
    name: 'HolyWorld',
    players: '8,920',
    timeAgo: '3 часа назад',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
        <path d="M3 18l3-10 4 5 2-7 2 7 4-5 3 10z" />
      </svg>
    ),
    art: (
      <svg viewBox="0 0 100 100">
        <path d="M20 70l8-40 15 20 7-25 7 25 15-20 8 40z" />
      </svg>
    )
  }
];

const RecentTestedServers = ({
  servers = [],
  isLoading,
  error,
  onServerSelect,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore
}) => {
  const { t, language } = useLanguage();
  const trackRef = useRef(null);

  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const hasDraggedRef = useRef(false);

  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingMoreRef = useRef(isLoadingMore);
  isLoadingMoreRef.current = isLoadingMore;

  const scrollDebounceTimerRef = useRef(null);

  const checkScrollNearEnd = useCallback(() => {
    if (!trackRef.current || !hasMoreRef.current || isLoadingMoreRef.current || !onLoadMoreRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
    if (scrollLeft + clientWidth >= scrollWidth - 300) {
      if (scrollDebounceTimerRef.current) return;
      scrollDebounceTimerRef.current = setTimeout(() => {
        scrollDebounceTimerRef.current = null;
      }, 300);
      onLoadMoreRef.current();
    }
  }, []);

  // Time offset sync
  const timeOffset = useMemo(() => {
    if (!servers || servers.length === 0) return 0;
    const serverTimes = servers
      .map(s => new Date(s.createdAt).getTime())
      .filter(t => !isNaN(t));
    if (serverTimes.length === 0) return 0;
    const maxServerTime = Math.max(...serverTimes);
    const localNow = Date.now();
    return maxServerTime > localNow + 30000 ? maxServerTime - localNow : 0;
  }, [servers]);

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const updateNow = () => setNow(new Date(Date.now() + timeOffset));
    updateNow();
    const timer = setInterval(updateNow, 30000);
    return () => clearInterval(timer);
  }, [timeOffset]);

  // Smooth scroll back to start when a new server card arrives at index 0
  const prevFirstIpRef = useRef(null);
  useEffect(() => {
    if (servers && servers.length > 0) {
      const firstIp = servers[0]?.ip;
      if (prevFirstIpRef.current && prevFirstIpRef.current !== firstIp) {
        if (trackRef.current && trackRef.current.scrollLeft > 0) {
          trackRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        }
      }
      prevFirstIpRef.current = firstIp;
    }
  }, [servers]);

  // Window-level smooth mouse dragging
  useEffect(() => {
    const handleWindowMouseMove = (e) => {
      if (!isDraggingRef.current || !trackRef.current) return;
      const x = e.pageX - trackRef.current.offsetLeft;
      const walk = x - startXRef.current;
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
      }
      trackRef.current.scrollLeft = scrollLeftRef.current - walk;
    };

    const handleWindowMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      checkScrollNearEnd();
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 50);
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [checkScrollNearEnd]);

  const handleMouseDown = (e) => {
    if (e.button !== 0 || !trackRef.current) return;
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - trackRef.current.offsetLeft;
    scrollLeftRef.current = trackRef.current.scrollLeft;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  const handleCardClick = (ip) => {
    if (hasDraggedRef.current) {
      return;
    }
    if (onServerSelect) {
      onServerSelect(ip);
    }
  };

  const scrollCarousel = (direction) => {
    if (trackRef.current) {
      const cardWidth = window.innerWidth < 640 ? 275 : 310;
      const scrollAmount = cardWidth * direction;
      trackRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      if (direction > 0) {
        setTimeout(checkScrollNearEnd, 260);
      }
    }
  };

  // Combine real servers if present, fallback to default presets
  const displayItems = useMemo(() => {
    if (servers && servers.length > 0) {
      return servers.map((s, idx) => ({
        ip: s.ip,
        name: s.hostname || s.ip,
        status: s.status || 'Online',
        players: s.players !== undefined ? `${s.players}` : '—',
        timeAgo: s.createdAt ? formatTimeAgo(s.createdAt, language, now) : 'недавно',
        icon: s.icon,
        art: DEFAULT_PRESETS[idx % DEFAULT_PRESETS.length].art
      }));
    }
    return DEFAULT_PRESETS;
  }, [servers, language, now]);

  return (
    <section className="mb-8 sm:mb-[50px] relative z-30" id="serversSection">
      {/* Header with Carousel Navigation Arrows */}
      <div className="flex items-center justify-between mb-3 sm:mb-4 px-1.5 sm:px-1">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 pr-2">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-wider sm:tracking-[0.12em] text-white truncate">
            {t('recentTestedServers') || 'Недавно протестированные серверы'}
          </span>
        </div>

        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <button
            onClick={() => scrollCarousel(-1)}
            className="w-[34px] h-[34px] rounded-full bg-white/[0.06] hover:bg-white text-white hover:text-black border border-white/[0.16] hover:border-white flex items-center justify-center transition-all duration-200 active:scale-95 cursor-pointer"
            aria-label="Назад"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            onClick={() => scrollCarousel(1)}
            className="w-[34px] h-[34px] rounded-full bg-white/[0.06] hover:bg-white text-white hover:text-black border border-white/[0.16] hover:border-white flex items-center justify-center transition-all duration-200 active:scale-95 cursor-pointer"
            aria-label="Вперед"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current fill-none" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Horizontal Track of Poster Cards */}
      <div
        ref={trackRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 pt-1 scrollbar-hide cursor-grab active:cursor-grabbing select-none touch-pan-x"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onMouseDown={handleMouseDown}
        onScroll={checkScrollNearEnd}
      >
        {isLoading && (!servers || servers.length === 0) ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={`skeleton-${idx}`}
              className="flex-none w-[260px] sm:w-[295px] h-[155px] sm:h-[165px] rounded-[18px] sm:rounded-[22px] p-4 sm:p-5 border border-white/[0.08] bg-[#08080c] animate-pulse flex flex-col justify-between"
            >
              <div className="flex items-center justify-between">
                <div className="w-[38px] h-[38px] rounded-xl bg-white/[0.08]" />
                <div className="w-[30px] h-[30px] rounded-full bg-white/[0.05]" />
              </div>
              <div className="flex flex-col gap-2">
                <div className="w-3/4 h-5 rounded-lg bg-white/[0.08]" />
                <div className="w-1/2 h-3.5 rounded-md bg-white/[0.05]" />
              </div>
            </div>
          ))
        ) : (
          <>
            <AnimatePresence initial={false}>
              {displayItems.map((item) => (
                <motion.div
                  key={item.ip.toLowerCase()}
                  layout
                  initial={{ opacity: 0, x: -60, scale: 0.92 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.25 } }}
                  transition={{
                    opacity: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                    x: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                    scale: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
                    layout: { duration: 0.45, ease: [0.16, 1, 0.3, 1] }
                  }}
                  onClick={() => handleCardClick(item.ip)}
                  onDragStart={(e) => e.preventDefault()}
                  className="flex-none w-[260px] sm:w-[295px] h-[155px] sm:h-[165px] rounded-[18px] overflow-hidden relative cursor-pointer flex flex-col justify-between p-4 sm:p-[18px_20px] border border-white/[0.08] hover:border-white group shadow-[0_4px_16px_rgba(0,0,0,0.4)] hover:-translate-y-1 hover:shadow-[0_16px_36px_-8px_rgba(0,0,0,0.9),0_0_20px_rgba(255,255,255,0.08)] active:scale-[0.98] transform-gpu will-change-transform bg-[#111319] select-none"
                  style={{
                    transition: 'border-color 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                  }}
                >
                {/* Background Art: Stretched & blurred server icon if available, fallback to art silhouette */}
                {typeof item.icon === 'string' && item.icon ? (
                  <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
                    <img
                      src={item.icon}
                      alt=""
                      draggable={false}
                      className="w-full h-full object-cover scale-105 opacity-25 group-hover:opacity-45 group-hover:scale-115 transform-gpu pointer-events-none"
                      style={{
                        filter: 'blur(3.5px) saturate(120%)',
                        transition: 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
                      }}
                    />
                  </div>
                ) : (
                  <div className="absolute -top-[15%] -right-[10%] w-[180px] h-[180px] pointer-events-none opacity-[0.12] group-hover:opacity-[0.28] group-hover:scale-110 group-hover:-rotate-3 transition-all duration-300 ease-out z-[1] [&_svg]:w-full [&_svg]:h-full [&_svg]:fill-white">
                    {item.art}
                  </div>
                )}

                {/* Gradient Overlay for high text readability */}
                <div
                  className="absolute inset-0 z-[2] pointer-events-none"
                  style={{
                    background: 'linear-gradient(180deg, rgba(8, 8, 12, 0.35) 0%, rgba(8, 8, 12, 0.85) 60%, #08080c 100%)'
                  }}
                />

                {/* Top Row: Mini Icon + Choose Arrow */}
                <div className="relative z-[3] flex items-center justify-between">
                  <div className="w-[38px] h-[38px] rounded-xl bg-white/[0.08] border border-white/25 flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.5)] overflow-hidden backdrop-blur-md">
                    {typeof item.icon === 'string' && item.icon ? (
                      <img src={item.icon} className="w-full h-full object-cover" alt="" draggable={false} />
                    ) : item.icon && typeof item.icon === 'object' ? (
                      item.icon
                    ) : (
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                        <path d="M4 4h4v6h8V4h4v16h-4v-6H8v6H4z" />
                      </svg>
                    )}
                  </div>

                  <div
                    className="w-[30px] h-[30px] rounded-full bg-[#181b24] group-hover:bg-white text-white/70 group-hover:text-black border border-white/10 flex items-center justify-center opacity-70 group-hover:opacity-100 group-hover:scale-105 transform-gpu"
                    style={{ transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)' }}
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>

                {/* Bottom Row: IP:PORT & Meta */}
                <div className="relative z-[3] flex flex-col gap-1">
                  <span className="font-mono text-[1.05rem] font-bold text-white tracking-tight truncate block">
                    {item.ip}
                  </span>
                  <div className="inline-flex items-center gap-1.5 text-xs text-white/65 font-medium">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${item.status?.toLowerCase() === 'offline' ? 'bg-[#ff4757] shadow-[0_0_6px_#ff4757]' : 'bg-[#2ed573] shadow-[0_0_6px_#2ed573]'}`} />
                    <span className="text-white font-semibold">
                      {item.status?.toLowerCase() === 'offline' ? 'Оффлайн' : `${item.players} онлайн`}
                    </span>
                    <span className="text-white/35 text-[10px]">•</span>
                    <span className="text-white/60 truncate">{item.timeAgo}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

            {/* Loading More Skeletons when scrolling near the end */}
            {isLoadingMore && (
              Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={`more-skeleton-${idx}`}
                  className="flex-none w-[295px] h-[165px] rounded-[18px] p-5 border border-white/[0.08] bg-[#08080c] animate-pulse flex flex-col justify-between select-none"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-[38px] h-[38px] rounded-xl bg-white/[0.08]" />
                    <div className="w-[30px] h-[30px] rounded-full bg-white/[0.05]" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="w-3/4 h-5 rounded-lg bg-white/[0.08]" />
                    <div className="w-1/2 h-3.5 rounded-md bg-white/[0.05]" />
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default RecentTestedServers;